
import { Challenge, CareerLevel } from '../../types/game';
import { SpecialEvent } from '../../types/specialEvent';
import { toast } from "@/components/ui/sonner";
import { getRandomChallengeForRole } from '../../utils/gameUtils';

// Fixed decision points at 30s, 75s, and 120s
export const calculateDecisionPoints = (yearDuration: number): number[] => {
  // Fixed timing points for better predictability
  // We'll use 30s, 75s, and 120s for a standard 3-minute year
  const fixedPoints = [30, 75, 120];
  
  // If year duration is different from standard 180s, scale the points accordingly
  if (yearDuration !== 180) {
    const scaleFactor = yearDuration / 180;
    return fixedPoints.map(point => Math.floor(point * scaleFactor));
  }
  
  return fixedPoints;
};

// Schedule decision events at specific times
export const scheduleDecision = (
  timePoint: number,
  index: number,
  yearsPassed: number,
  currentChallenge: Challenge | null,
  currentSpecialEvent: SpecialEvent | null,
  decisionsThrottled: boolean,
  decisionTimerFired: Record<string, boolean>,
  recentChallenges: string[],
  selectedRole: any,
  careerLevel: CareerLevel,
  skills: any[],
  setCurrentChallenge: (challenge: Challenge) => void,
  setDecisionTimerFired: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  setRecentChallenges: React.Dispatch<React.SetStateAction<string[]>>
) => {
  const pointKey = `year_${yearsPassed}_point_${index}`;
  
  // Debug the condition check to understand why decisions aren't firing
  console.log(`Decision check at ${timePoint}s:`, {
    alreadyFired: decisionTimerFired[pointKey] || false,
    hasCurrentChallenge: !!currentChallenge,
    hasSpecialEvent: !!currentSpecialEvent,
    isThrottled: decisionsThrottled,
    year: yearsPassed,
    recentChallenges
  });
  
  // Only fire if this specific decision point hasn't fired before
  if (!decisionTimerFired[pointKey] && !currentChallenge && !currentSpecialEvent && !decisionsThrottled) {
    console.log(`Creating decision at ${timePoint}s mark`);
    
    // Create a new challenge
    if (selectedRole) {
      // CRITICAL FIX: Ensure we're getting new challenges that haven't been used recently
      const newChallenge = getRandomChallengeForRole(
        selectedRole, 
        careerLevel, 
        skills, 
        yearsPassed,
        recentChallenges
      );
      
      // Debug the selected challenge
      console.log('Selected new challenge:', {
        id: newChallenge.id,
        title: newChallenge.title, 
        isRoleSpecific: selectedRole.specialChallenges.some(c => c.id === newChallenge.id)
      });
      
      // Force a small delay to ensure UI state updates
      setTimeout(() => {
        console.log('Setting new challenge:', newChallenge);
        setCurrentChallenge(newChallenge);
      
        // Mark this decision point as fired
        setDecisionTimerFired(prev => ({ ...prev, [pointKey]: true }));
        
        // Add to recent challenges to prevent repetition
        setRecentChallenges(prev => {
          const updated = [newChallenge.id, ...prev];
          // Keep only the 10 most recent challenges (increased from 5)
          return updated.slice(0, 10);
        });
        
        // Add toast notification with different styling based on challenge difficulty
        const difficultyLabels = {
          1: "Basic",
          2: "Standard",
          3: "Complex",
          4: "Advanced",
          5: "Expert"
        };
        
        const difficultyLabel = difficultyLabels[newChallenge.difficulty] || "Standard";
        
        toast(`New ${difficultyLabel} Decision: ${newChallenge.title}`, {
          description: "A new challenge requires your attention!",
          duration: 5000, // Increased duration for better visibility
        });
      }, 100);
    }
  } else {
    console.log(`Skipping decision at ${timePoint}s mark because:`, 
      decisionTimerFired[pointKey] ? 'already fired' : 'not fired before',
      currentChallenge ? 'challenge exists' : 'no current challenge',
      currentSpecialEvent ? 'special event exists' : 'no special event',
      decisionsThrottled ? 'throttled' : 'not throttled');
  }
};

// Clean up all existing decision timers
export const clearDecisionTimers = (timers: NodeJS.Timeout[]): void => {
  timers.forEach(timer => clearTimeout(timer));
};
